import { useEffect, useState } from 'react';
import type {
  EvidenceDossier,
  ToolCallRecord,
} from '../../lib/artefacts-api';
import type { ArtefactsState } from '../../hooks/useBriefArtefacts';

/**
 * Evidence Dossier panel — renders the agent's toolCalls as evidence cards
 * (Phase 5d-1 follow-up commit 5).
 *
 * Each tool call gets a card showing tool name + duration + cost + the
 * pertinent input/output fields. Per-tool friendly summaries surface the
 * most relevant data; the raw JSON stays available behind a "Show raw"
 * toggle for the cases where the friendly view isn't enough.
 */
interface Props {
  state: ArtefactsState<EvidenceDossier>;
  noAgentRunYet: boolean;
  onExpand: () => void;
  s3Prefix: string | null;
  /** Brief status so we can distinguish a Stage 1 poor_fit short-circuit
   *  ("agent ran, didn't gather Stage 2 evidence") from a genuinely
   *  un-run Brief ("agent never ran"). */
  briefStatus?: string;
}

export function EvidenceDossierPanel({ state, noAgentRunYet, onExpand, s3Prefix, briefStatus }: Props) {
  const [open, setOpen] = useState(false);

  // Trigger lazy-load when the user opens the panel.
  useEffect(() => {
    if (open && state.status === 'idle' && !noAgentRunYet) {
      onExpand();
    }
  }, [open, state.status, noAgentRunYet, onExpand]);

  return (
    <section className="portal-brief-section">
      <button
        type="button"
        className="portal-collapsible"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>
          📂 Evidence Dossier {s3Prefix ? `· ${s3Prefix}` : ''}
        </span>
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="portal-collapsible-body">
          <Body state={state} noAgentRunYet={noAgentRunYet} briefStatus={briefStatus} />
        </div>
      )}
    </section>
  );
}

function Body({ state, noAgentRunYet, briefStatus }: { state: Props['state']; noAgentRunYet: boolean; briefStatus?: string }) {
  if (noAgentRunYet || state.status === 'no-agent-run-yet') {
    // Stage 1 poor_fit short-circuit: agent ran but skipped Stage 2, so
    // no tool calls were made and no S3 triad was written. The Stage 1
    // verdict is the only evidence and it's already shown above in the
    // Stage 1 holistic assessment section. Distinguish this case from a
    // genuinely un-run Brief so the operator doesn't think the agent failed.
    if (briefStatus === 'rejected_poor_fit') {
      return (
        <p className="portal-help">
          Stage 1 short-circuit — the agent classified this Brief as Poor Fit and skipped
          the Stage 2 evidence gathering (tool calls, scoring) per ADR-031 §4. The Stage 1
          summary above is the full agent reasoning. To gather Stage 2 evidence anyway,
          Reassess the Brief — if the applicant updated their description to read as a
          services business, Stage 1 may now route to Possible / Strong Fit and run
          Stage 2.
        </p>
      );
    }
    return (
      <p className="portal-help">
        No evidence artefacts have been written for this Brief yet. The pow-agent Lambda
        writes the S3 triad (<code>evidence-dossier.json</code> +{' '}
        <code>score-breakdown.json</code> + <code>agent-trace.json</code>) at the end of
        each successful Stage 1 + Stage 2 run. If the agent is still running, this section
        will populate when the Brief transitions out of <code>pending_validation</code>.
      </p>
    );
  }
  if (state.status === 'loading' || state.status === 'idle') {
    return <p className="portal-help">Loading evidence dossier…</p>;
  }
  if (state.status === 'error') {
    return <p className="portal-help">Could not load evidence: {state.error}</p>;
  }
  const dossier = state.data;
  if (!dossier || !dossier.toolCalls || dossier.toolCalls.length === 0) {
    return <p className="portal-help">No tool calls recorded for this run.</p>;
  }
  const totalDuration = dossier.toolCalls.reduce((sum, t) => sum + t.durationMs, 0);
  const totalCost = dossier.toolCalls.reduce((sum, t) => sum + t.costEstimateUsd, 0);
  return (
    <div className="portal-evidence-dossier">
      <div className="portal-evidence-dossier-meta">
        <span>
          <strong>{dossier.toolCalls.length}</strong> tool calls · {Math.round(totalDuration)}ms
          total · ${totalCost.toFixed(4)} estimated
        </span>
        <span className="portal-evidence-dossier-trace">traceId: <code>{dossier.traceId}</code></span>
      </div>
      <ul className="portal-evidence-dossier-list">
        {dossier.toolCalls.map((call, idx) => (
          <ToolCallCard key={idx} call={call} index={idx} />
        ))}
      </ul>
    </div>
  );
}

function ToolCallCard({ call, index }: { call: ToolCallRecord; index: number }) {
  const [showRaw, setShowRaw] = useState(false);
  const summary = summariseToolCall(call);
  return (
    <li className={`portal-tool-call${call.error ? ' portal-tool-call-error' : ''}`}>
      <div className="portal-tool-call-header">
        <span className="portal-tool-call-index">#{index + 1}</span>
        <code className="portal-tool-call-name">{call.tool}</code>
        <span className="portal-tool-call-meta">
          {Math.round(call.durationMs)}ms · ${call.costEstimateUsd.toFixed(4)}
        </span>
      </div>
      {call.error && <div className="portal-tool-call-error-msg">Error: {call.error}</div>}
      {summary && <div className="portal-tool-call-summary">{summary}</div>}
      <button
        type="button"
        className="portal-tool-call-raw-toggle"
        onClick={() => setShowRaw((v) => !v)}
      >
        {showRaw ? 'Hide raw' : 'Show raw'}
      </button>
      {showRaw && (
        <div className="portal-tool-call-raw">
          <div className="portal-tool-call-raw-section">
            <h4>Input</h4>
            <pre>{JSON.stringify(call.input, null, 2)}</pre>
          </div>
          <div className="portal-tool-call-raw-section">
            <h4>Output</h4>
            <pre>{JSON.stringify(call.output, null, 2)}</pre>
          </div>
        </div>
      )}
    </li>
  );
}

/**
 * Friendly per-tool summary. Each tool has a slightly different output
 * shape per ADR-027 §10.2; the friendly summary surfaces the most
 * pertinent field(s) so staff can scan without expanding raw JSON.
 */
function summariseToolCall(call: ToolCallRecord): React.ReactNode {
  const input = call.input as Record<string, unknown> | null;
  const output = call.output as Record<string, unknown> | null;
  if (input === null || output === null) return null;

  switch (call.tool) {
    case 'fetch_url': {
      return (
        <>
          <strong>URL:</strong> <code>{String(input.url ?? '—')}</code>
          {output.title !== undefined && (
            <>
              {' · '}<strong>Title:</strong> {String(output.title)}
            </>
          )}
        </>
      );
    }
    case 'google_search': {
      const results = output.results as unknown[] | undefined;
      return (
        <>
          <strong>Query:</strong> {String(input.query ?? '—')} ·{' '}
          <strong>Results:</strong> {results?.length ?? 0}
        </>
      );
    }
    /**
     * ⭐⭐ THE `outcome` FIELD IS READ BEFORE `verified`, AND THAT ORDER IS THE FIX.
     *
     * Until 17 September 2026 this cell rendered `output.verified === false` as a ✕. The
     * tool returned `verified: false` for BOTH "the register says this business is not
     * registered" AND "we never asked" — and it had never once asked, because none of
     * nexa/abr-api-key, nexa/nzbn-api-key or the US key exist and the body was a
     * placeholder besides. So every Brief ever assessed showed an operator a ✕ against
     * the applicant's registration number: a check that never ran, rendered as a check
     * that failed. An operator reading a queue cannot tell those apart, and the ✕ is the
     * one that costs a real applicant a decision.
     *
     * `verify_business_registration` now returns the shared register client's three-state
     * outcome. "unavailable" (no key, no US state, brand-only name) and "unsupported" (no
     * adapter for that jurisdiction — UK, CAN) are facts about US, not about them, and
     * render as "not checked" with the reason. Only "answered" earns a ✓ or a ✕.
     *
     * ⚠ THE `undefined` BRANCH IS FOR HISTORICAL TRACES, NOT DEFENSIVENESS. Every Brief
     * assessed before this build is archived in S3 with an output carrying no `outcome`
     * key at all, and those dossiers are still opened from the Partners queue. They are
     * shown as "not checked" — which is exactly what they were.
     */
    case 'verify_business_registration': {
      const verdictOutcome = typeof output.outcome === 'string' ? output.outcome : undefined;
      const answered = verdictOutcome === 'answered';
      const reason = typeof output.reason === 'string' ? output.reason : undefined;
      return (
        <>
          <strong>Jurisdiction:</strong> {String(input.jurisdiction ?? '—')} ·{' '}
          <strong>Reg #:</strong> <code>{String(input.registrationNumber ?? '—')}</code>{' · '}
          {answered ? (
            <>
              <strong>Verified:</strong> {output.verified === true ? '✓' : '✕'}
              {typeof output.source === 'string' && <> {' · '}<strong>Register:</strong> {output.source}</>}
            </>
          ) : (
            <>
              <strong>Not checked</strong>
              {reason && <> — {reason}</>}
            </>
          )}
        </>
      );
    }
    case 'check_snippet_activity': {
      return (
        <>
          <strong>accountId:</strong> <code>{String(input.accountId ?? '—')}</code>{' · '}
          <strong>Events:</strong> {String(output.eventCount ?? 0)} ·{' '}
          <strong>Active domains:</strong> {String(output.activeDomains ?? 0)}
        </>
      );
    }
    case 'check_account_internals': {
      return (
        <>
          <strong>accountId:</strong> <code>{String(input.accountId ?? '—')}</code>{' · '}
          <strong>Domains:</strong> {String(output.domainCount ?? 0)} ·{' '}
          <strong>Tier:</strong> {String(output.planTier ?? '—')}
        </>
      );
    }
    case 'whois_lookup': {
      return (
        <>
          <strong>Domain:</strong> <code>{String(input.domain ?? '—')}</code>{' · '}
          <strong>Registered:</strong> {String(output.registrationDate ?? '—')}
        </>
      );
    }
    case 'check_backlink': {
      return (
        <>
          <strong>Target:</strong> <code>{String(input.targetDomain ?? '—')}</code>{' · '}
          <strong>Found:</strong> {output.found === true ? '✓' : '✕'}
        </>
      );
    }
    case 'validate_customer_number': {
      return (
        <>
          <strong>CN:</strong> <code>{String(input.customerNumber ?? '—')}</code>{' · '}
          <strong>Valid:</strong> {output.valid === true ? '✓' : '✕'}
        </>
      );
    }
    default:
      return null;
  }
}
